import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Card, CardContent } from 'components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import PageHeading from '../../../components/heading/index';
import Home, { checkEnabled, getPaymentFilterList } from './tabs/home';
import IntegratedAtm from './tabs/atm';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import {
  PAYMENT_CONFIG,
  PaymentSolutionTab,
  TabVisibility,
  TabVisibilityKey,
  VISIBILITY_KEY_MAP,
  PAYMENT_TYPE_MAP,
  TabConfig,
} from 'state/paymentConfig/type';
import IntegratedDebit from './tabs/debit';
import IntegratedCredit from './tabs/credit';
import IntegratedAch from './tabs/ach';

const TABS_CONFIG: TabConfig[] = [
  {
    value: PaymentSolutionTab.HOME,
    label: 'Home',
    component: Home,
  },
  {
    value: PaymentSolutionTab.ATM,
    label: 'Integrated ATM',
    component: IntegratedAtm,
    showKey: TabVisibilityKey.ATM,
  },
  {
    value: PaymentSolutionTab.DEBIT,
    label: 'Integrated Debit',
    component: IntegratedDebit,
    showKey: TabVisibilityKey.DEBIT,
  },
  {
    value: PaymentSolutionTab.CREDIT,
    label: 'Integrated Credit',
    component: IntegratedCredit,
    showKey: TabVisibilityKey.CREDIT,
  },
  {
    value: PaymentSolutionTab.ACH,
    label: 'Integrated ACH',
    component: IntegratedAch,
    showKey: TabVisibilityKey.ACH,
  },
];

const enableActivePaymentTab = (
  paymentMethod: string,
  setTabVisibility: Dispatch<SetStateAction<TabVisibility>>
) => {
  const key = VISIBILITY_KEY_MAP[paymentMethod];
  if (key) {
    setTabVisibility((prev) => ({ ...prev, [key]: true }));
  }
};

const ConfigurationSettings = (): JSX.Element => {
  const [activeTab, setActiveTab] = useState(PaymentSolutionTab.HOME);

  const [defaultProvider, setDefaultProvider] =
    useState<PAYMENT_CONFIG.IProcessors>();

  const {
    paymentConfig,
    toFetchPaymentSolutions,
    toFetchDefaultProviders,
    toFetchDefaultConfigurations,
    updatedFilterList,
    resetPaymentDeviceList,
  } = usePaymentConfig();

  const { paymentSolutions, defaultProcessor } = paymentConfig;

  const [tabVisibility, setTabVisibility] = useState<TabVisibility>({
    [TabVisibilityKey.ATM]: false,
    [TabVisibilityKey.CREDIT]: false,
    [TabVisibilityKey.DEBIT]: false,
    [TabVisibilityKey.ACH]: false,
  });

  useEffect(() => {
    Promise.all([
      toFetchPaymentSolutions(),
      toFetchDefaultProviders(),
      toFetchDefaultConfigurations(),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPaymentTab = (tabNo: PaymentSolutionTab): string => {
    return PAYMENT_TYPE_MAP[tabNo] ?? PaymentSolutionTab.ATM;
  };

  const toggleTab = (
    tabNo: PaymentSolutionTab,
    paymentSolution: Array<PAYMENT_CONFIG.IProcessors> | null,
    processorDefault: PAYMENT_CONFIG.IFetchDefaultProcessor
  ) => {
    resetPaymentDeviceList();
    setActiveTab(tabNo);
    const paymentType = getPaymentTab(tabNo);

    const tempList =
      paymentSolution && paymentSolution.length > 0
        ? getPaymentFilterList(
            paymentSolution,
            paymentType as PaymentSolutionTab
          )
        : [];
    updatedFilterList(tempList);

    const getDefaultProvider = processorDefault?.data?.filter((item) =>
      item?.paymentType?.includes(paymentType)
    );
    const defaultProviderEnableCheck = checkEnabled(
      getDefaultProvider?.[0],
      tempList
    );
    if (defaultProviderEnableCheck && getDefaultProvider?.[0]?.processorName) {
      setDefaultProvider({
        paymentMethod: getDefaultProvider?.[0]?.paymentType ?? '',
        processorId: getDefaultProvider?.[0]?.processorId ?? '',
        processorName: getDefaultProvider?.[0]?.processorName ?? '',
        isEnabled: true,
      });
    } else {
      resetPaymentDeviceList();
      setDefaultProvider(undefined);
    }
  };
  useEffect(() => {
    const processors = paymentSolutions?.processor?.processors;
    if (processors?.length) {
      processors.forEach((item) => {
        if (item?.isEnabled) {
          enableActivePaymentTab(item?.paymentMethod || '', setTabVisibility);
        }
      });
    }
  }, [paymentSolutions]);

  return (
    <Card className="border-0 shadow-none">
      <CardContent className="p-0">
        <PageHeading
          level="h1"
          heading="Payment configuration"
          subHeading="Configure your payment solutions, providers, and device settings."
        />
        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            toggleTab(
              value as PaymentSolutionTab,
              paymentSolutions?.processor?.processors,
              defaultProcessor
            )
          }
          className="w-full"
        >
          <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 h-auto gap-5">
            {TABS_CONFIG.map((tab) => {
              const shouldShow = !tab.showKey || tabVisibility[tab.showKey];

              if (!shouldShow) return null;

              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-treez-green data-[state=active]:text-gray-blue-80 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 text-muted-foreground hover:text-gray-blue-80"
                >
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {TABS_CONFIG.map((tab) => {
            const Component = tab.component;
            const shouldShow = !tab.showKey || tabVisibility[tab.showKey];

            if (!shouldShow) return null;

            return (
              <TabsContent key={tab.value} value={tab.value} className="mt-4">
                <Component
                  activeTab={activeTab}
                  setActiveTab={
                    tab.value === PaymentSolutionTab.HOME
                      ? setActiveTab
                      : undefined
                  }
                  defaultProvider={defaultProvider}
                  setDefaultProvider={setDefaultProvider}
                />
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ConfigurationSettings;
